import { Box, Grid2, Tooltip, Typography, alpha } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

import ArrowIcon from 'src/assets/icons/arrow-down.svg';
import InfoIcon from 'src/assets/icons/info-circle.svg';
import TimePicker from 'src/components/BasicTimePicker';
import { AdminDateTimePicker } from 'src/components/NodeSaleForm/AdminDateTimePicker';
import { colors } from 'src/global/styles/colors';

const ArrowButton = () => (
  <Typography component={'span'} color='white'>
    <ArrowIcon />
  </Typography>
);

const SalePeriod = ({ readOnly }: { readOnly?: boolean }) => {
  const {
    setValue,
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <>
      <Typography className='text-head_s font-gotham text-white'>Sale Period</Typography>
      <Grid2 container spacing={2}>
        <Grid2 size={6}>
          <Box width={'100%'}>
            <Typography paddingBottom={1} color={colors.neutral200} className='text-body_m font-gotham'>
              Start Time
            </Typography>
            <Controller
              control={control}
              name='startedDate'
              render={({ field }) => {
                return (
                  <TimePicker
                    isHideHint
                    isError={!!errors.startedDate}
                    errorMsg={errors?.startedDate?.message as string}
                    value={new Date(field.value)}
                    slots={{ openPickerIcon: ArrowButton }}
                    readOnly={readOnly}
                    onChange={date => {
                      field.onChange(date);
                      setValue('closingTime', undefined);
                    }}
                  />
                );
              }}
            />
          </Box>
        </Grid2>
        <Grid2 size={6}>
          <Box width={'100%'}>
            <Typography
              paddingBottom={1}
              display={'flex'}
              alignItems={'center'}
              flexDirection={'row'}
              gap={1}
              color={colors.neutral200}
              className='text-body_m font-gotham'>
              Start Date
              <Tooltip
                title={
                  <Typography className='text-body_s font-gotham'>
                    If you don’t enter a specific start date, the system will automatically launch the sale as soon as
                    you submit it, meaning it becomes active immediately.
                  </Typography>
                }
                placement='top'
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: alpha('#04060BE5', 0.9),
                      color: 'white',
                      borderRadius: '4px',
                      padding: '8px 12px',
                    },
                  },
                  arrow: {
                    sx: {
                      color: alpha('#04060BE5', 0.9),
                    },
                  },
                }}>
                <Typography
                  display={'flex'}
                  alignItems={'center'}
                  className='text-body_m'
                  color='#767581'
                  component={'span'}>
                  <InfoIcon />
                </Typography>
              </Tooltip>
            </Typography>
            <Controller
              control={control}
              name='startedDate'
              render={({ field }) => {
                return (
                  <AdminDateTimePicker
                    isHideHint
                    isError={!!errors.startedDate}
                    errorMsg={errors?.startedDate?.message as string}
                    readOnly={readOnly}
                    value={new Date(field.value)}
                    inputRef={field.ref}
                    onChange={date => {
                      field.onChange(date);
                      setValue('closingDate', undefined);
                    }}
                    views={['year', 'month', 'day']}
                  />
                );
              }}
            />
          </Box>
        </Grid2>
      </Grid2>
      <Grid2 container spacing={2}>
        <Grid2 size={6}>
          <Box width={'100%'}>
            <Typography paddingBottom={1} color={colors.neutral200} className='text-body_m font-gotham'>
              End Time
            </Typography>
            <Controller
              control={control}
              name='closingDate'
              render={({ field }) => {
                return (
                  <TimePicker
                    isHideHint
                    isError={!!errors.closingDate}
                    errorMsg={errors?.closingDate?.message as string}
                    value={new Date(field.value)}
                    slots={{ openPickerIcon: ArrowButton }}
                    onChange={date => {
                      field.onChange(date);
                    }}
                    readOnly={readOnly}
                  />
                );
              }}
            />
          </Box>
        </Grid2>
        <Grid2 size={6}>
          <Box width={'100%'}>
            <Typography
              paddingBottom={1}
              display={'flex'}
              alignItems={'center'}
              gap={1}
              className='text-body_m text-neutral-300 font-gotham'>
              End Date
              <Tooltip
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: alpha('#04060BE5', 0.9),
                      color: 'white',
                      borderRadius: '4px',
                      padding: '8px 12px',
                    },
                  },
                  arrow: {
                    sx: {
                      color: alpha('#04060BE5', 0.9),
                    },
                  },
                }}
                title={
                  <Typography className='text-body_s font-gotham'>
                    If you leave this blank, the sale will continue without a set end time, making it available
                    indefinitely until you decide to stop it manually.
                  </Typography>
                }
                placement='top'
                arrow>
                <Typography
                  display={'flex'}
                  alignItems={'center'}
                  className='text-body_m'
                  color='#767581'
                  component={'span'}>
                  <InfoIcon />
                </Typography>
              </Tooltip>
            </Typography>
            <Controller
              control={control}
              name='closingDate'
              render={({ field }) => {
                return (
                  <AdminDateTimePicker
                    isHideHint
                    isError={!!errors.closingDate}
                    errorMsg={errors?.closingDate?.message as string}
                    readOnly={readOnly}
                    value={new Date(field.value)}
                    inputRef={field.ref}
                    onChange={date => {
                      field.onChange(date);
                    }}
                    views={['year', 'month', 'day']}
                  />
                );
              }}
            />
          </Box>
        </Grid2>
      </Grid2>
    </>
  );
};

export default SalePeriod;
